import type { CardOut } from "@/api/cards";
import type { KpiEntry } from "@/api/kpi";
import { t } from "@/i18n";
import { fmtInt, fmtMoney } from "@/lib/utils";

interface BugunSeridiProps {
  kpis: Record<string, KpiEntry> | null;
  cards: CardOut[];
  onOpenDetail: (kpiId: string, title: string) => void;
}

function n(v: unknown): number {
  const x = Number(v);
  return Number.isFinite(x) ? x : 0;
}

/** Alan gercekten SAYI degilse "—" gosterir — fmtInt(0) ile KARISTIRILMAZ. */
function sayiGoster(v: unknown, bicimle: (n: number) => string): string {
  return typeof v === "number" ? bicimle(v) : "—";
}

interface BantOge {
  key: string;
  cardId: string;
  label: string;
  deger: string;
  altDeger?: string;
}

/**
 * "Bugün" şeridi — eski `#summaryBar` (gradyan bant, Space Grotesk sayılar).
 * Her öğe ilgili kart canlıysa tıklanabilir → KpiDetailModal.
 */
export function BugunSeridi({ kpis, cards, onOpenDetail }: BugunSeridiProps) {
  const entry = (id: string): KpiEntry | undefined => {
    const v = kpis?.[id];
    return v && typeof v === "object" ? (v as KpiEntry) : undefined;
  };
  const cardOf = (id: string): CardOut | undefined => cards.find((c) => c.id === id);

  const bs = entry("bugunku_siparis");
  const yu = entry("yeni_uye");
  const bd = entry("bugun_destek");
  const ob = entry("odeme_bekleyen");

  const ogeler: BantOge[] = [
    { key: "siparis", cardId: "bugunku_siparis", label: t("ozet.bant.siparis"), deger: sayiGoster(bs?.count, fmtInt) },
    { key: "ciro", cardId: "bugunku_siparis", label: t("ozet.bant.ciro"), deger: sayiGoster(bs?.today_revenue, fmtMoney) },
    { key: "yeni_uye", cardId: "yeni_uye", label: t("ozet.bant.yeni_uye"), deger: sayiGoster(yu?.count, fmtInt) },
    { key: "acilan_destek", cardId: "bugun_destek", label: t("ozet.bant.acilan_destek"), deger: sayiGoster(bd?.count, fmtInt) },
    {
      key: "odeme_bekleyen",
      cardId: "odeme_bekleyen",
      label: t("ozet.bant.odeme_bekleyen"),
      deger: sayiGoster(ob?.count, fmtInt),
      altDeger: n(ob?.count) > 0 ? fmtMoney(n(ob?.total_amount)) : undefined,
    },
  ];

  return (
    <div className="summary-bar" data-testid="ozet-bugun-seridi">
      <span className="self-center border-r-2 border-line pr-3 text-sm font-semibold text-brand-primary">
        {t("ozet.bant.baslik")}
      </span>
      {ogeler.map((oge) => {
        const card = cardOf(oge.cardId);
        const tiklanabilir = card?.state === "live";
        return (
          <button
            key={oge.key}
            type="button"
            disabled={!tiklanabilir}
            onClick={() => tiklanabilir && onOpenDetail(oge.cardId, card?.title ?? oge.label)}
            className="min-h-[44px] rounded-lg px-1 text-left transition-colors enabled:hover:bg-card/70 disabled:cursor-default"
          >
            <div className="text-xs font-medium text-muted">{oge.label}</div>
            <div className="num text-xl font-bold leading-tight text-ink">{oge.deger}</div>
            {oge.altDeger && <div className="text-xs text-muted">{oge.altDeger}</div>}
          </button>
        );
      })}
    </div>
  );
}
